import { IChangeVisibility } from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureSettings/StructureSettings.schema";
import { IFormValues } from "src/Pages/Company/OrganizationStructure/Forms/EditStructureForm/EditStructureForm.schema";
import { ICreateStructure } from "src/Pages/Company/OrganizationStructure/ConfigureStructure/CreateStructure/CreateStructure.scheme";
import {
  OrganizationStructureApiClient,
  EmployeeApiClient,
} from "src/Pages/Company/OrganizationStructure/apiClient/OrganizationStructureApiClient";
import {
  IAreasOrgchartData,
  IEmployeesOrgchartData,
  IAreaMember,
  ICommonIQReturnType,
  IAreasSearch,
} from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/StructureView.models";
import { IAreaData } from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureAreas/StructureAreas.models";
import { ContentType } from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureAreas/DataManagment/DataManagment.models";
import {
  Employee,
  ICreateArea,
} from "src/Pages/Company/OrganizationStructure/Areas/Area.models";
import {
  IModuleSettings,
  TModuleSettingsPayload,
} from "src/Pages/Company/OrganizationStructure/ModuleSettings/moduleSettings.models";
import { IDeleteArea } from "src/Pages/Company/OrganizationStructure/api/useDeleteArea";

export const fetchStructureData = async (
  companyId: string,
  structureId: string,
  {
    signal,
  }: {
    signal: AbortSignal | undefined;
  }
) => {
  const { data } = await OrganizationStructureApiClient.get(
    `/v1/companies/${companyId}/structures/${structureId}`,
    {
      signal,
    }
  );
  return data;
};

export const changeVisibility = async (
  companyId: string,
  structureId: string,
  switchState: IChangeVisibility
) => {
  const { data } = await OrganizationStructureApiClient.put(
    `/v1/companies/${companyId}/structures/${structureId}/visibility`,
    switchState
  );
  return data;
};

export const saveEditStructureForm = async (
  companyId: string,
  structureId: string,
  formData: IFormValues
) => {
  const { data } = await OrganizationStructureApiClient.put(
    `/v1/companies/${companyId}/structures/${structureId}`,
    formData
  );
  return data;
};

export const createStructure = async (
  companyId: string,
  formData: ICreateStructure
) => {
  const { data } = await OrganizationStructureApiClient.post(
    `/v1/companies/${companyId}/structures`,
    formData
  );
  return data;
};

export const fetchListOfStructures = async (
  companyId: string,
  {
    signal,
  }: {
    signal: AbortSignal | undefined;
  }
) => {
  const data = await OrganizationStructureApiClient.get(
    `/v1/companies/${companyId}/structures`,
    {
      signal,
    }
  );
  return data;
};

export const createArea = async (
  companyId: string,
  structureId: string,
  formData: ICreateArea
) => {
  const { data } = await OrganizationStructureApiClient.post(
    `/v1/companies/${companyId}/structures/${structureId}/areas`,
    formData
  );
  return data;
};

export const fetchAreas = async (
  companyId: string,
  structureId: string,
  {
    signal,
  }: {
    signal: AbortSignal | undefined;
  },
  filters: string | null,
  page: number,
  sortType: string
): Promise<IAreaData> => {
  const { data } = await OrganizationStructureApiClient.get(
    `/v1/companies/${companyId}/structures/${structureId}/areas`,
    {
      signal,
      params: {
        filters,
        page,
        sort: `areaName:${sortType}`,
      },
    }
  );
  return data;
};

export const fetchArea = async (
  companyId: string,
  structureId: string,
  areaId: string,
  {
    signal,
    queryParams,
  }: {
    signal: AbortSignal | undefined;
    queryParams?: Record<string, string>;
  }
) => {
  const { data } = await OrganizationStructureApiClient.get(
    `/v1/companies/${companyId}/structures/${structureId}/areas/${areaId}`,
    {
      signal,
      params: queryParams,
    }
  );
  return data;
};

export const changeArea = async (
  companyId: string,
  structureId: string,
  areaId: string,
  formData: ICreateArea
) => {
  const { data } = await OrganizationStructureApiClient.put(
    `v1/companies/${companyId}/structures/${structureId}/areas/${areaId}`,
    formData
  );
  return data;
};

export const fetchEmployeeOrgchart = async (
  companyId: string,
  structureId: string
): Promise<IEmployeesOrgchartData> => {
  const { data } = await OrganizationStructureApiClient.get(
    `v1/companies/${companyId}/structures/${structureId}/employees-org-chart`
  );
  return data;
};

export const fetchAreasOrgchart = async (
  companyId: string,
  structureId: string
): Promise<IAreasOrgchartData> => {
  const { data } = await OrganizationStructureApiClient.get(
    `v1/companies/${companyId}/structures/${structureId}/areas-org-chart`
  );
  return data;
};

export const fetchAreaMembers = async ({
  areaId,
  pageParam,
  companyId,
  structureId,
}: {
  areaId: string;
  pageParam: string | null;
  companyId: string;
  structureId: string;
}): Promise<ICommonIQReturnType<IAreaMember>> => {
  const { data } = await OrganizationStructureApiClient.get(
    `/v1/companies/${companyId}/structures/${structureId}/areas/${areaId}/members`,
    {
      params: {
        cursor: pageParam,
      },
    }
  );
  return data;
};

export const fetchAreaHierarchy = async (
  companyId: string,
  structureId: string,
  areaId: string,
  {
    signal,
  }: {
    signal: AbortSignal | undefined;
  }
) => {
  const { data } = await OrganizationStructureApiClient.get(
    `/v1/companies/${companyId}/structures/${structureId}/areas/${areaId}/hierarchy`,
    {
      signal,
    }
  );
  return data;
};

export interface IEmployeesList {
  content: Employee[];
  totalElements?: number;
  nextCursor: string;
  size: number;
}

interface ICommonInfinityQParams {
  companyId: string;
  structureId: string | undefined;
  pageParam: string | null;
  filters: string | null;
}

export const fetchEmployeesList = async ({
  structureId,
  companyId,
  pageParam,
  filters,
}: ICommonInfinityQParams): Promise<IEmployeesList> => {
  const { data } = await OrganizationStructureApiClient.get(
    `/v1/companies/${companyId}/structures/${structureId}/employees`,
    { params: { cursor: pageParam, filters } }
  );
  return data;
};

export const fetchAreasSearch = async ({
  structureId,
  companyId,
  pageParam,
  filters,
}: ICommonInfinityQParams): Promise<ICommonIQReturnType<IAreasSearch>> => {
  const { data } = await OrganizationStructureApiClient.get(
    `/v1/companies/${companyId}/structures/${structureId}/areas/search`,
    { params: { cursor: pageParam, filters } }
  );
  return data;
};

export const downloadAreasData = async (
  companyId: string,
  structureId: string,
  contentType: ContentType
) =>
  // eslint-disable-next-line
  await OrganizationStructureApiClient.get(
    `v1/companies/${companyId}/structures/${structureId}/areas/export`,
    { data: {}, headers: { "Content-Type": contentType }, responseType: "blob" }
  );

export const downloadStructureData = async (
  companyId: string,
  structureId: string,
  contentType: ContentType
) =>
  // eslint-disable-next-line
  await OrganizationStructureApiClient.get(
    `v1/companies/${companyId}/structures/${structureId}/employees/export`,
    { data: {}, headers: { "Content-Type": contentType }, responseType: "blob" }
  );

export const fetchAreaMembership = async (
  companyId: string,
  employeeId: string | null,
  {
    signal,
  }: {
    signal: AbortSignal | undefined;
  }
) => {
  const { data } = await OrganizationStructureApiClient.get(
    `/v1/companies/${companyId}/employees/${employeeId}/area-membership`,
    {
      signal,
    }
  );
  return data;
};

export const fetchModuleSettings = async (
  companyId: string,
  {
    signal,
  }: {
    signal: AbortSignal | undefined;
  }
): Promise<IModuleSettings> => {
  const { data } = await OrganizationStructureApiClient.get(
    `/v1/companies/${companyId}/modules-configuration`,
    { signal }
  );

  return data;
};

export const changeModuleSettings = async (
  companyId: string,
  payload: TModuleSettingsPayload
) => {
  const { data } = await OrganizationStructureApiClient.put(
    `/v1/companies/${companyId}/modules-configuration`,
    payload
  );

  return data;
};

export const deleteArea = async ({
  companyId,
  structureId,
  areaId,
}: IDeleteArea) => {
  const { data } = await OrganizationStructureApiClient.delete(
    `/v1/companies/${companyId}/structures/${structureId}/areas/${areaId}`
  );
  return data;
};

export const fetchEmployee = async (
  employeeId: string
): Promise<{ email: string | null; firstName: string; lastName: string }> => {
  const { data } = await EmployeeApiClient.get(
    `/v1/rest/employees/${employeeId}`
  );

  return data;
};
